import React, { useState, useEffect, useRef } from 'react';
import { usePageEditor } from './PageEditorContext';
import { Edit3, Check, X, Loader2 } from 'lucide-react';

// Editable Section - wraps an entire section and allows editing its content
const EditableSection = ({ 
  sectionId, 
  children, 
  className = '',
  defaultContent = null 
}) => {
  const { isEditMode, getContent, updateContent, currentPage } = usePageEditor();
  const [isEditing, setIsEditing] = useState(false);
  const [localHtml, setLocalHtml] = useState('');
  const editorRef = useRef(null);
  
  // Get saved content or use children as default
  const savedContent = getContent(sectionId, 'html', null);
  
  useEffect(() => {
    if (savedContent) {
      setLocalHtml(savedContent);
    }
  }, [savedContent]);

  const handleStartEditing = () => {
    if (!savedContent && editorRef.current) {
      // If no saved content, capture current HTML
      setLocalHtml(editorRef.current.innerHTML);
    }
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      updateContent(sectionId, 'html', html);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (savedContent) {
      setLocalHtml(savedContent);
    }
    setIsEditing(false);
  };

  // Not in edit mode - render children or saved content
  if (!isEditMode) {
    if (savedContent) {
      return (
        <div 
          className={className}
          dangerouslySetInnerHTML={{ __html: savedContent }}
        />
      );
    }
    return <div className={className}>{children}</div>;
  }

  // In edit mode but not actively editing this section
  if (!isEditing) {
    return (
      <div 
        className={`${className} relative group cursor-pointer`}
        onClick={handleStartEditing}
        data-testid={`editable-section-${sectionId}`}
      >
        {/* Hover overlay */}
        <div className="absolute inset-0 border-2 border-dashed border-transparent group-hover:border-[#E91E8C] group-hover:bg-[#E91E8C]/5 rounded-lg transition-all pointer-events-none z-10">
          <div className="absolute top-2 right-2 bg-[#E91E8C] text-white px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-sm font-medium shadow-lg">
            <Edit3 className="w-4 h-4" />
            Click to edit this section
          </div>
        </div>
        
        {/* Content */}
        <div ref={editorRef}>
          {savedContent ? (
            <div dangerouslySetInnerHTML={{ __html: savedContent }} />
          ) : (
            children
          )}
        </div>
      </div>
    );
  }

  // Actively editing this section
  return (
    <div className={`${className} relative`}>
      {/* Editing toolbar */}
      <div className="sticky top-[108px] z-20 flex items-center justify-between bg-white border-2 border-[#E91E8C] rounded-t-lg p-3 shadow-lg">
        <span className="text-sm font-medium text-gray-700">
          Editing: <span className="text-[#E91E8C] capitalize">{sectionId.replace(/-/g, ' ')}</span>
        </span>
        <div className="flex gap-2">
          <button
            onClick={handleCancel}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#E91E8C] text-white rounded-lg hover:bg-[#d11a7d] text-sm font-medium"
          >
            <Check className="w-4 h-4" />
            Save Section
          </button>
        </div>
      </div>
      
      {/* Editable content area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="border-2 border-[#E91E8C] border-t-0 rounded-b-lg p-4 bg-white min-h-[200px] focus:outline-none"
        dangerouslySetInnerHTML={{ __html: localHtml || '' }}
        onInput={(e) => setLocalHtml(e.currentTarget.innerHTML)}
      />
      
      {/* Editing tips */}
      <div className="mt-2 text-xs text-gray-500 flex gap-4">
        <span>💡 Select text to format it</span>
        <span>↵ Press Enter for new paragraph</span>
        <span>⌘/Ctrl + B for bold</span>
      </div>
    </div>
  );
};

export default EditableSection;
